import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DollarSign, Clock3, CheckCircle2, ArrowLeft, LogOut, Trash2, Bell, KeyRound, CreditCard, ExternalLink, Check, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import QuickAddGaps from "../components/fyllr/QuickAddGaps";
import SessionManager from "../components/fyllr/SessionManager";
import ContentStudio from "../components/fyllr/ContentStudio";
import PartnerBilling from "../components/fyllr/PartnerBilling";
import VenueProfileEditor from "../components/fyllr/VenueProfileEditor";
import { SalonEarnings } from "../components/fyllr/SalonEarnings";
import { NotificationBell } from "../components/fyllr/NotificationBell";
import AddGapDialog from "../components/fyllr/AddGapDialog";
import { getOpenings, getSalons, getSalonRecovery, listBookings, listSalonBookingRequests, approveSalonBookingRequest, declineSalonBookingRequest, deleteGap, getMe, salonLogout, updateSalonNotifications, salonSetPassword, salonConnectOnboard, salonConnectStatus, getSalonAnalytics, getSalonNotifications, markSalonNotificationsRead } from "../lib/api";

export default function SalonAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [salon, setSalon] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [requestAction, setRequestAction] = useState(null);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [emailNotifOptIn, setEmailNotifOptIn] = useState(true);
  const [savingPref, setSavingPref] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMe();
        if (cancelled) return;
        setSession({ slug: me.salon_slug, name: me.name, email: me.email });
        setSmsOptIn(Boolean(me.sms_opt_in));
        setEmailNotifOptIn(me.email_notifications_opt_in !== false);
        setHasPassword(Boolean(me.has_password));
        // Fetch analytics in background
        getSalonAnalytics(30).then(setAnalytics).catch(() => {});
      } catch (_e) {
        navigate("/salon/login");
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const [postOpen, setPostOpen] = useState(false);
  const refresh = async (slug) => {
    if (!slug) return;
    try {
      const [salonsList, openings, recoveryData, bookingsData, requestsData] = await Promise.all([
        getSalons(),
        getOpenings({ salon_slug: slug }),
        getSalonRecovery(slug, 7),
        listBookings({ salon_slug: slug }),
        listSalonBookingRequests(),
      ]);
      setSalon(salonsList.find((x) => x.slug === slug) || null);
      setGaps(openings);
      setRecovery(recoveryData);
      setBookings(bookingsData);
      setBookingRequests(requestsData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("SalonAdmin.refresh failed:", e);
    }
  };

  useEffect(() => {
    if (session?.slug) refresh(session.slug);
  }, [session]);

  const onLogout = async () => {
    try { await salonLogout(); } catch (_e) { /* non-fatal */ }
    navigate("/salon/login");
  };

  const onDeleteGap = async (id) => {
    try {
      await deleteGap(id);
      toast.success("Gap removed.");
      refresh(session?.slug);
    } catch {
      toast.error("Could not remove gap.");
    }
  };
  const handleBookingRequest = async (bookingId, action) => { setRequestAction(`${bookingId}-${action}`); try { if (action === "approve") await approveSalonBookingRequest(bookingId); else await declineSalonBookingRequest(bookingId); toast.success(action === "approve" ? "Booking approved." : "Booking declined and capacity released."); refresh(session?.slug); } catch (error) { toast.error(error?.response?.data?.detail || "Could not update booking request."); } finally { setRequestAction(null); } };

  const onToggleNotification = async (key, next) => {
    const prev = key === "sms_opt_in" ? smsOptIn : emailNotifOptIn;
    // Optimistic update
    if (key === "sms_opt_in") setSmsOptIn(next);
    else setEmailNotifOptIn(next);
    setSavingPref(key);
    try {
      await updateSalonNotifications({ [key]: next });
      toast.success(
        next
          ? key === "sms_opt_in" ? "SMS alerts on." : "Email alerts on."
          : key === "sms_opt_in" ? "SMS alerts off." : "Email alerts off."
      );
    } catch {
      // Revert on failure
      if (key === "sms_opt_in") setSmsOptIn(prev);
      else setEmailNotifOptIn(prev);
      toast.error("Couldn't save. Try again.");
    } finally {
      setSavingPref(null);
    }
  };

  if (!session) return null;

  const upcoming = gaps;
  const booked = bookings.filter((b) => ["confirmed", "authorized", "captured"].includes(b.status));
  const pendingRequests = bookingRequests.filter((request) => request.status === "pending_approval");

  return (
    <div className="pt-14 md:pt-16 min-h-screen pb-20" data-testid="salon-admin-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-8 md:py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/salon" className="text-xs text-ink/55 hover:text-ink transition-colors inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={14} strokeWidth={1.5} /> All studios
            </Link>
            <p className="text-[11px] tracking-[0.22em] uppercase text-mocha">Studio dashboard</p>
            <h1 className="mt-2 font-serif text-3xl md:text-4xl text-ink leading-tight" data-testid="admin-salon-name">
              {salon?.name || session.name}
            </h1>
            <p className="text-sm text-ink/55">{salon?.suburb} · {session.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell fetchFeed={getSalonNotifications} markRead={markSalonNotificationsRead} testId="salon-notifications" />
            <button
              onClick={onLogout}
              className="text-xs text-ink/60 hover:text-ink transition-colors inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-sand hover:border-mocha"
              data-testid="admin-logout"
            >
              <LogOut size={12} strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>

        {new URLSearchParams(location.search).get("onboarding") === "complete" && <section className="mt-7 border border-burgundy/30 bg-burgundy/5 px-5 py-4" data-testid="salon-onboarding-complete"><p className="font-serif text-2xl text-espresso" data-testid="salon-onboarding-complete-title">Your studio is in.</p><p className="mt-1 text-sm text-ink/65" data-testid="salon-onboarding-complete-message">{new URLSearchParams(location.search).get("billing") === "unavailable" ? "Your dashboard is ready. We’ll email you when membership billing is ready to activate." : "Your dashboard is ready. You can finish your membership whenever you’re ready."}</p></section>}

        <section className="mt-7 border border-sand bg-sand/20 px-5 py-4" data-testid="salon-auto-sync-note"><p className="font-serif text-xl text-espresso">Auto-sync coming soon.</p><p className="mt-1 text-sm text-ink/65">FYLLR will connect directly to your booking system so gap slots publish automatically when you get a cancellation. We’ll notify you when your system is supported.</p></section>

        <div className="mt-8 grid gap-8 lg:grid-cols-[190px_minmax(0,1fr)]">
          <SalonDashboardSidebar />
          <main className="min-w-0">
        {/* Quick Add — primary workflow */}
        <section className="" id="post-gap" data-testid="admin-quick-add">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif text-xl text-ink">Add a gap</h2>
            <button type="button" onClick={() => setPostOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-espresso px-4 py-2 text-sm text-bone hover:bg-ink transition-colors" data-testid="salon-post-opening-btn">Post an opening</button>
          </div>
          <p className="text-xs text-ink/45 mb-3">Post with a date and time, or tap any time below to publish in one tap. Openings go live instantly at the top of the deals feed.</p>
          <AddGapDialog open={postOpen} onClose={() => setPostOpen(false)} onCreated={() => refresh(session?.slug)} salonSlug={session.slug} defaultSuburb={salon?.suburb} />
          <QuickAddGaps
            salonSlug={session.slug}
            suburb={salon?.suburb}
            gaps={gaps}
            onChange={() => refresh(session?.slug)}
          />
        </section>

        {/* ---- Analytics Dashboard ---- */}
        <div id="dashboard" data-testid="salon-dashboard-overview"><AnalyticsDashboard
          analytics={analytics}
          recovery={recovery}
          upcomingCount={upcoming.length}
          bookedCount={booked.length}
        /></div>

        <section className="mt-12" id="bookings" data-testid="salon-booking-requests"><div className="flex items-baseline justify-between mb-4"><h2 className="font-serif text-xl text-ink">Booking requests</h2><p className="text-xs text-ink/45">Approve before a client is confirmed</p></div>{pendingRequests.length ? <div className="bg-bone border border-sand/70 rounded-2xl divide-y divide-sand/70 overflow-hidden">{pendingRequests.map((request) => <article key={request.id} className="grid gap-4 px-5 py-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center" data-testid={`salon-booking-request-${request.id}`}><div><p className="font-serif text-xl text-ink">{request.customer_name}</p><p className="mt-1 text-sm text-ink/60">{request.service} · {request.when}</p><p className="mt-1 text-xs text-ink/45">{request.customer_email}{request.spots > 1 ? ` · ${request.spots} spots` : ""}</p></div><div className="flex flex-wrap gap-2"><button type="button" disabled={Boolean(requestAction)} onClick={() => handleBookingRequest(request.id, "approve")} className="inline-flex items-center gap-2 rounded-full bg-espresso px-4 py-2 text-sm text-bone hover:bg-ink disabled:opacity-50" data-testid={`salon-booking-approve-${request.id}`}><Check size={14} />Approve</button><button type="button" disabled={Boolean(requestAction)} onClick={() => handleBookingRequest(request.id, "decline")} className="inline-flex items-center gap-2 rounded-full border border-sand px-4 py-2 text-sm text-ink/70 hover:border-espresso hover:text-espresso disabled:opacity-50" data-testid={`salon-booking-decline-${request.id}`}><X size={14} />Decline</button></div></article>)}</div> : <div className="bg-bone border border-dashed border-sand rounded-2xl p-8 text-center" data-testid="salon-booking-requests-empty"><p className="font-serif text-xl text-ink">No bookings yet — your requests will appear here once you post your first opening.</p><a href="#post-gap" className="mt-4 inline-flex min-h-11 items-center rounded-full bg-espresso px-5 text-sm text-bone" data-testid="booking-empty-post-opening">Post your first opening</a></div>}</section>

        <SalonEarnings onChange={() => refresh(session?.slug)} />

        {/* Upcoming gaps */}
        <section className="mt-12">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-xl text-ink">All live gaps</h2>
            <p className="text-xs text-ink/45">{upcoming.length} live in marketplace</p>
          </div>
          {upcoming.length === 0 ? (
            <div className="bg-bone border border-dashed border-sand rounded-2xl p-10 text-center">
              <p className="font-serif text-xl text-ink">No live gaps yet.</p>
              <p className="mt-2 text-sm text-ink/55">Use the Quick Add card above to publish your first slot.</p>
            </div>
          ) : (
            <div className="bg-bone border border-sand/70 rounded-2xl divide-y divide-sand/70 overflow-hidden">
              {upcoming.map((g) => (
                <div key={g.id} className="grid grid-cols-12 gap-3 items-center px-5 py-4" data-testid={`admin-upcoming-${g.id}`}>
                  <span className="col-span-12 sm:col-span-5 font-serif text-base text-ink truncate">{g.service}</span>
                  <span className="col-span-6 sm:col-span-3 text-sm text-ink/55 truncate inline-flex items-center gap-1.5">
                    <Clock3 size={12} strokeWidth={1.5} className="text-mocha" />
                    {g.time}
                  </span>
                  <span className="col-span-3 sm:col-span-2 font-serif text-base text-espresso">${g.offer_price}</span>
                  <span className="col-span-3 sm:col-span-2 flex justify-end">
                    {g.id?.startsWith("g_") && (
                      <button
                        onClick={() => onDeleteGap(g.id)}
                        className="p-1.5 rounded-full hover:bg-sand text-ink/55 hover:text-ink transition-colors"
                        title="Remove"
                        data-testid={`admin-delete-${g.id}`}
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Booking history (from analytics) */}
        <section className="mt-12" data-testid="admin-booking-history">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-xl text-ink">Booking history</h2>
            <p className="text-xs text-ink/45">Last 30 days</p>
          </div>
          {!analytics?.recent?.length && booked.length === 0 ? (
            <div className="bg-bone border border-dashed border-sand rounded-2xl p-8 text-center text-sm text-ink/55">
              No bookings yet — your requests will appear here once you post your first opening.
            </div>
          ) : (
            <div className="bg-bone border border-sand/70 rounded-2xl divide-y divide-sand/70 overflow-hidden">
              {(analytics?.recent || booked).map((b) => (
                <div key={b.id} className="grid grid-cols-12 gap-3 items-center px-5 py-4" data-testid={`admin-booked-${b.id}`}>
                  <span className="col-span-12 sm:col-span-4 font-serif text-base text-ink truncate">{b.customer_name}</span>
                  <span className="col-span-6 sm:col-span-3 text-sm text-ink/55 truncate">{b.customer_email}</span>
                  <span className="col-span-3 sm:col-span-2 text-sm text-ink/55">{b.category || b.treatment_category || ""}</span>
                  <span className="col-span-2 sm:col-span-1 font-serif text-base text-espresso">${Math.round(b.amount)}</span>
                  <span className="col-span-1 sm:col-span-2 flex justify-end">
                    <span className={`text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full ${
                      b.status === "authorized" || b.status === "captured" ? "bg-emerald-100 text-emerald-700" :
                      b.status === "pending" ? "bg-amber-100 text-amber-700" :
                      "bg-red-100 text-red-700"
                    }`}>{b.status}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
        {/* ---- Partner Session Manager ---- */}
        <section className="mt-12" data-testid="admin-sessions-section">
          <SessionManager />
        </section>

        <div id="venue-profile"><VenueProfileEditor /></div>

        {/* ---- Content Studio (submit content for FYLLR socials) ---- */}
        <ContentStudio />

        {/* ---- Partner membership billing ---- */}
        <div id="partner-membership"><PartnerBilling salonSlug={session.slug} email={session.email} /></div>

        {/* Notifications */}
        <section className="mt-12" id="notifications" data-testid="admin-notifications">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-xl text-ink">Notifications</h2>
            <p className="text-xs text-ink/45">How we reach you</p>
          </div>
          <div className="bg-bone border border-sand/70 rounded-2xl p-6 md:p-7">
            <div className="flex items-start gap-3 mb-5">
              <span className="mt-0.5 w-8 h-8 rounded-full bg-sand/70 flex items-center justify-center text-mocha shrink-0">
                <Bell size={14} strokeWidth={1.5} />
              </span>
              <div>
                <p className="font-serif text-base text-ink leading-tight">Booking &amp; calendar alerts</p>
                <p className="mt-1 text-sm text-ink/55 max-w-md">
                  Choose how FYLLR notifies you when a gap fills, a client cancels, or your calendar syncs.
                </p>
              </div>
            </div>

            <ToggleRow
              testId="notif-sms-toggle"
              label="SMS alerts"
              description="Instant text when a gap fills or your calendar syncs. AU mobile required."
              checked={smsOptIn}
              disabled={savingPref === "sms_opt_in"}
              onChange={(v) => onToggleNotification("sms_opt_in", v)}
            />
            <div className="h-px bg-sand/60 my-4" />
            <ToggleRow
              testId="notif-email-toggle"
              label="Email alerts"
              description="Booking confirmations, cancellations and weekly performance recap."
              checked={emailNotifOptIn}
              disabled={savingPref === "email_notifications_opt_in"}
              onChange={(v) => onToggleNotification("email_notifications_opt_in", v)}
            />
          </div>
        </section>

        {/* ---- Security (Password) ---- */}
        <section className="mt-10" id="security" data-testid="admin-security">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-xl text-ink">Security</h2>
            <p className="text-xs text-ink/45">{hasPassword ? "Password set" : "No password yet"}</p>
          </div>
          <PasswordCard hasPassword={hasPassword} onSuccess={() => setHasPassword(true)} />
        </section>

        {/* ---- Stripe Connect (Payouts) ---- */}
        <section className="mt-10" id="payouts" data-testid="admin-connect">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-xl text-ink">Payouts</h2>
            <p className="text-xs text-ink/45">Stripe Connect</p>
          </div>
          <ConnectCard />
        </section>
          </main>
        </div>
      </div>
    </div>
  );
}

function SalonDashboardSidebar() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const primary = [["post-gap", "Post a Gap"], ["bookings", "Bookings"], ["dashboard", "Dashboard"]];
  const settings = [["venue-profile", "Venue Profile"], ["partner-membership", "Partner Membership"], ["payouts", "Payouts"], ["notifications", "Notifications"], ["security", "Security"]];
  return <aside className="self-start border border-sand bg-bone p-3 lg:sticky lg:top-24" data-testid="salon-dashboard-sidebar"><p className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] text-mocha">Primary</p><nav className="space-y-1" aria-label="Salon dashboard primary navigation">{primary.map(([id, label]) => <a key={id} href={`#${id}`} className="block px-3 py-2 text-sm text-ink/70 transition-colors hover:bg-sand/35 hover:text-espresso" data-testid={`salon-sidebar-${id}`}>{label}</a>)}</nav><div className="mt-3 border-t border-sand pt-3"><button type="button" onClick={() => setSettingsOpen((open) => !open)} className="flex w-full items-center justify-between px-3 py-2 text-sm text-ink/70 hover:text-espresso" aria-expanded={settingsOpen} data-testid="salon-sidebar-settings-toggle"><span>Settings</span><ChevronDown size={15} className={`transition-transform ${settingsOpen ? "rotate-180" : ""}`} /></button>{settingsOpen && <nav className="mt-1 space-y-1" aria-label="Salon dashboard settings">{settings.map(([id, label]) => <a key={id} href={`#${id}`} className="block px-3 py-2 text-sm text-ink/60 transition-colors hover:bg-sand/35 hover:text-espresso" data-testid={`salon-sidebar-${id}`}>{label}</a>)}</nav>}</div></aside>;
}


function AnalyticsDashboard({ analytics, recovery, upcomingCount, bookedCount }) {
  const { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } = require("recharts");

  const totalRevenue = analytics?.total_revenue ?? (recovery?.total_recovered ?? 0);
  const totalBookings = analytics?.total_bookings ?? (recovery?.bookings_filled ?? 0);
  const avgValue = analytics?.avg_booking_value ?? 0;
  const fillRate = recovery?.fill_rate ?? 0;
  const dailyData = analytics?.daily || [];
  const categoryData = analytics?.categories || [];

  const COLORS = ["#3B0B0B", "#681719", "#9B3A35", "#AA8B7A", "#F6F0E8", "#1E0908", "#681719", "#9B3A35"];

  return (
    <section className="mt-8 space-y-4" data-testid="admin-analytics">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-espresso text-bone rounded-2xl p-5" data-testid="kpi-revenue">
          <div className="flex items-center gap-1.5 text-bone/50">
            <DollarSign size={12} strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.22em] uppercase">Revenue</span>
          </div>
          <p className="mt-3 font-serif font-bold text-4xl md:text-5xl leading-none text-bone">${Math.round(totalRevenue).toLocaleString()}</p>
          <p className="mt-1 text-[11px] text-bone/45">Last 30 days</p>
        </div>
        <div className="bg-bone border border-sand/70 rounded-2xl p-5" data-testid="kpi-bookings">
          <p className="text-[10px] tracking-[0.22em] uppercase text-mocha">Bookings</p>
          <p className="mt-3 font-serif font-bold text-4xl md:text-5xl text-espresso leading-none">{totalBookings}</p>
          <p className="mt-1 text-[11px] text-ink/40">confirmed</p>
        </div>
        <div className="bg-bone border border-sand/70 rounded-2xl p-5" data-testid="kpi-avg">
          <p className="text-[10px] tracking-[0.22em] uppercase text-mocha">Avg value</p>
          <p className="mt-3 font-serif font-bold text-4xl md:text-5xl text-espresso leading-none">${Math.round(avgValue)}</p>
          <p className="mt-1 text-[11px] text-ink/40">per booking</p>
        </div>
        <div className="bg-bone border border-sand/70 rounded-2xl p-5" data-testid="kpi-fill-rate">
          <p className="text-[10px] tracking-[0.22em] uppercase text-mocha">Fill rate</p>
          <p className="mt-3 font-serif font-bold text-4xl md:text-5xl text-espresso leading-none">{fillRate}%</p>
          <p className="mt-1 text-[11px] text-ink/40">{upcomingCount} live · {bookedCount} filled</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Daily Revenue Bar Chart */}
        <div className="md:col-span-2 bg-bone border border-sand/70 rounded-2xl p-5 md:p-6" data-testid="chart-daily-revenue">
          <p className="text-[10px] tracking-[0.22em] uppercase text-mocha mb-4">Daily revenue · 30 days</p>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="d" tick={{ fontSize: 10, fill: "#AA8B7A" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#AA8B7A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: "#3B0B0B", border: "none", borderRadius: 8, color: "#F6F0E8", fontSize: 13 }}
                  labelStyle={{ color: "#B88C89", fontSize: 11 }}
                  formatter={(v) => [`$${v}`, "Revenue"]}
                />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]} maxBarSize={28}>
                  {dailyData.map((_, i) => (
                    <Cell key={i} fill={i === dailyData.length - 1 ? "#3B0B0B" : "#AA8B7A"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex flex-col items-center justify-center gap-3 px-6 text-center text-sm text-ink/55" data-testid="chart-daily-revenue-empty"><span>Your first booking will show up here.</span><a href="#post-gap" className="inline-flex min-h-11 items-center rounded-full border border-espresso px-5 text-sm text-espresso" data-testid="daily-revenue-post-opening">Post your first opening</a></div>
          )}
        </div>

        {/* Category Split */}
        <div className="bg-bone border border-sand/70 rounded-2xl p-5 md:p-6" data-testid="chart-category-split">
          <p className="text-[10px] tracking-[0.22em] uppercase text-mocha mb-4">By category</p>
          {categoryData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={130}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="revenue"
                    nameKey="category"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {categoryData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                  contentStyle={{ background: "#3B0B0B", border: "none", borderRadius: 8, color: "#F6F0E8", fontSize: 13 }}
                    formatter={(v) => [`$${v}`, "Revenue"]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {categoryData.slice(0, 5).map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between text-[12px]">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-ink/70">{c.category}</span>
                    </span>
                    <span className="text-ink/50">{c.count} · ${Math.round(c.revenue)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[200px] flex flex-col items-center justify-center gap-3 px-6 text-center text-sm text-ink/55" data-testid="chart-category-empty"><span>Your first booking will show up here.</span><a href="#post-gap" className="inline-flex min-h-11 items-center rounded-full border border-espresso px-5 text-sm text-espresso" data-testid="category-revenue-post-opening">Post your first opening</a></div>
          )}
        </div>
      </div>
    </section>
  );
}


function ToggleRow({ testId, label, description, checked, disabled, onChange }) {
  return (
    <label
      htmlFor={testId}
      className={`flex items-start justify-between gap-4 cursor-pointer group ${disabled ? "opacity-60 pointer-events-none" : ""}`}
      data-testid={`${testId}-row`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-serif text-base text-ink leading-tight">{label}</p>
        <p className="mt-1 text-sm text-ink/55">{description}</p>
      </div>
      <button
        id={testId}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        data-testid={testId}
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors border ${
          checked ? "bg-espresso border-espresso" : "bg-sand/70 border-sand"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-bone shadow-sm transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </label>
  );
}

function PasswordCard({ hasPassword, onSuccess }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (pw !== confirm) { setError("Passwords don't match."); return; }
    setSaving(true);
    try {
      await salonSetPassword(pw);
      toast.success(hasPassword ? "Password updated." : "Password set. You can now sign in with email + password.");
      setPw(""); setConfirm("");
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-bone border border-sand/70 rounded-2xl p-6 md:p-7" data-testid="password-card">
      <div className="flex items-start gap-3 mb-5">
        <span className="mt-0.5 w-8 h-8 rounded-full bg-sand/70 flex items-center justify-center text-mocha shrink-0">
          <KeyRound size={14} strokeWidth={1.5} />
        </span>
        <div>
          <p className="font-serif text-base text-ink leading-tight">
            {hasPassword ? "Change password" : "Set a password"}
          </p>
          <p className="mt-1 text-sm text-ink/55 max-w-md">
            {hasPassword
              ? "Update your salon login password. Min. 10 characters."
              : "Add a password so you can sign in without a magic link. Min. 10 characters."}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4 max-w-sm">
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">
            {hasPassword ? "New password" : "Password"}
          </span>
          <input
            type="password"
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Min. 10 characters"
            className="w-full bg-bone border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
            data-testid="password-input"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Confirm password</span>
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat password"
            className="w-full bg-bone border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
            data-testid="password-confirm"
          />
        </label>
        {error && <p className="text-sm text-red-600" data-testid="password-error">{error}</p>}
        <button
          type="submit"
          disabled={saving}
          className={`inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-5 py-2.5 text-sm transition-colors ${saving ? "opacity-60" : "hover:bg-ink"}`}
          data-testid="password-submit"
        >
          {saving ? "Saving…" : hasPassword ? "Update password" : "Set password"}
        </button>
      </form>
    </div>
  );
}


function ConnectCard() {
  const [status, setStatus] = useState(null); // null = loading
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await salonConnectStatus();
        if (!cancelled) setStatus(data);
      } catch (_e) {
        if (!cancelled) setStatus({ status: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onConnect = async () => {
    setConnecting(true);
    try {
      const data = await salonConnectOnboard();
      window.location.href = data.url;
    } catch (err) {
      toast.error("Could not start Stripe onboarding. Try again.");
      setConnecting(false);
    }
  };

  if (!status) {
    return (
      <div className="bg-bone border border-sand/70 rounded-2xl p-6 md:p-7 animate-pulse" data-testid="connect-card-loading">
        <div className="h-4 bg-sand/50 rounded w-1/3 mb-3" />
        <div className="h-3 bg-sand/30 rounded w-2/3" />
      </div>
    );
  }

  const isActive = status.status === "active";
  const isPending = status.status === "pending" || status.status === "incomplete";

  return (
    <div className="bg-bone border border-sand/70 rounded-2xl p-6 md:p-7" data-testid="connect-card">
      <div className="flex items-start gap-3 mb-5">
        <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-sand/70 text-mocha"}`}>
          <CreditCard size={14} strokeWidth={1.5} />
        </span>
        <div>
          <p className="font-serif text-base text-ink leading-tight">
            {isActive ? "Payouts active" : isPending ? "Onboarding in progress" : "Connect payouts"}
          </p>
          <p className="mt-1 text-sm text-ink/55 max-w-md">
            {isActive
              ? `Stripe Express connected. You receive 87% of each booking — FYLLR keeps a 13% platform fee.`
              : isPending
                ? "You've started Stripe onboarding but it's not complete yet. Finish setup to start receiving payouts."
                : "Connect your Stripe account to receive payouts directly when clients book your openings. FYLLR takes a 13% platform fee."}
          </p>
        </div>
      </div>

      {isActive ? (
        <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="connect-active-badge">
          <CheckCircle2 size={14} strokeWidth={1.5} />
          <span>Payouts enabled · charges enabled</span>
        </div>
      ) : (
        <button
          onClick={onConnect}
          disabled={connecting}
          className={`inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-5 py-2.5 text-sm transition-colors ${connecting ? "opacity-60" : "hover:bg-ink"}`}
          data-testid="connect-onboard-btn"
        >
          {connecting ? "Opening Stripe…" : isPending ? "Continue setup" : "Connect with Stripe"}
          <ExternalLink size={13} strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}

